<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import companyApi from '@/api/modules/companyApi';
import personApi from '@/api/modules/personApi';
import CardSearchResult from '@/components/cards/CardSearchResult.vue';
import Skeleton from 'primevue/skeleton';
import Paginator from 'primevue/paginator';
import RecentActivityList from '@/components/dashboard/RecentActivityList.vue';
import { isValidCpf, isValidCnpj } from '@/utils/validators';
import { normalizeCpf, normalizeCnpj } from '@/utils/formatters';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import type { PersonSearchResult } from '@/types/person';
import type { CompanySearchResults } from '@/types/company';

// --- Props ---

const props = defineProps<{
  type: 'company' | 'person';
}>();

// --- Interfaces ---

interface SearchType {
  label: string;
  value: 'name' | 'tax_id';
  icon: string;
}

type SearchResultItem = CompanySearchResults | PersonSearchResult;

// --- Component State ---

const searchTerm = ref('');
const validationError = ref<string | null>(null);
const filterActiveOnly = ref(false);
const searchResults = ref<SearchResultItem[] | null>(null);
const loading = ref(false);
const resultCardClasses = "bg-white p-4 rounded-xl border border-slate-200 transition-all duration-200 hover:shadow-lg hover:border-indigo-300 cursor-pointer h-[90px]";

// Pagination State
const offset = ref(0);
const limit = ref(10);
const totalResults = ref(0);

const isCompany = computed(() => props.type === 'company');

const title = computed(() => isCompany.value ? 'Consultar Empresa por CNPJ' : 'Consultar Pessoa por CPF');

const searchTypes = computed<SearchType[]>(() => {
  if (isCompany.value) {
    return [
      { label: 'Nome da Empresa', value: 'name', icon: 'domain' },
      { label: 'CNPJ da empresa', value: 'tax_id', icon: 'badge' },
    ];
  } else {
    return [
      { label: 'CPF da pessoa', value: 'tax_id', icon: 'badge' },
    ];
  }
});

const searchType = ref<SearchType>(searchTypes.value[0]!);

watch(searchTypes, (newSearchTypes) => {
  if (newSearchTypes.length > 0 && !newSearchTypes.some(st => st.value === searchType.value.value)) {
    searchType.value = newSearchTypes[0]!;
  }
}, { immediate: true });

const placeholderText = computed(() => {
  if (searchType.value.value === 'tax_id') {
    return isCompany.value ? 'Buscar por número do CNPJ...' : 'Buscar por número do CPF...';
  }
  return 'Buscar por Razão Social ou Nome Fantasia...';
});

const router = useRouter();

// --- Methods ---

const performSearch = async (newOffset: number = 0): Promise<void> => {
  searchTerm.value = searchTerm.value.trim();
  validationError.value = null;

  if (searchType.value.value === 'tax_id') {
    if (isCompany.value) {
      if (!isValidCnpj(searchTerm.value.toUpperCase())) {
        validationError.value = 'CNPJ inválido. Deve conter 8 ou 14 caracteres.';
        return;
      }
    } else {
      if (!isValidCpf(searchTerm.value)) {
        validationError.value = 'CPF inválido. Deve conter 11 dígitos.';
        return;
      }
      // For valid 11-digit CPF, call lookup to get/create UUID and navigate directly
      const cleanCpf = normalizeCpf(searchTerm.value);
      if (cleanCpf.length === 11) {
        loading.value = true;
        try {
          const response = await personApi.lookup(searchTerm.value, 0, 1);
          if (response.data.length > 0) {
            const person = response.data[0] as PersonSearchResult;
            router.push(`/person/${person.uuid}`);
            return;
          }
        } catch (error) {
          console.error('Error looking up person:', error);
          validationError.value = getApiErrorMessage(
            error,
            'Erro ao buscar pessoa. Tente novamente.',
            { genericOnServerError: true }
          );
        } finally {
          loading.value = false;
        }
        return;
      }
    }
  }

  loading.value = true;
  offset.value = newOffset;

  try {
    const response = isCompany.value
      ? await companyApi.getCompanyByCnpjOrName(searchTerm.value, offset.value, limit.value, filterActiveOnly.value, searchType.value.value)
      : await personApi.lookup(searchTerm.value, offset.value, limit.value);

    searchResults.value = response.data;
    totalResults.value = response.total;

  } catch (error) {
    console.error(`Error searching ${props.type}:`, error);
    validationError.value = getApiErrorMessage(
      error,
      `Erro ao buscar ${isCompany.value ? 'empresa' : 'pessoa'}. Tente novamente.`,
      { genericOnServerError: true }
    );
    searchResults.value = null;
    totalResults.value = 0;
  } finally {
    loading.value = false;
  }
};

const onPageChange = (event: any) => {
    performSearch(event.first);
};

const onViewDetails = (item: SearchResultItem) => {
  if (isCompany.value) {
    const company = item as CompanySearchResults;
    // Slice to 8 characters to navigate directly to the base CNPJ URL
    // This prevents a 'replace' redirect in the router guard that skips history
    const cleanCnpj = normalizeCnpj(company.tax_id_full).slice(0, 8);
    router.push(`/company/${cleanCnpj}`);
  }
  else {
    const person = item as PersonSearchResult;
    router.push(`/person/${person.uuid}`);
  }
};
</script>

<template>
  <div>
    <!-- HEADER AND AI-STYLE SEARCH PROMPT -->
    <header class="mb-10">
      <h1 class="text-3xl font-bold text-slate-900">{{ title }}</h1>
      <div class="mt-6">
        <div class="relative flex items-center p-1.5 rounded-xl shadow-lg bg-white border border-slate-200 focus-within:ring-2 focus-within:ring-indigo-500 transition-all">
          <Select
            v-model="searchType"
            :options="searchTypes"
            optionLabel="label"
            class="w-50 !border-0 !shadow-none p-variant-filled"
            :disabled="!isCompany"
          >
            <template #value="{ value }: { value: SearchType }">
              <div v-if="value" class="flex items-center gap-2">
                <span class="material-icons text-gray-500">{{ value.icon }}</span>
                <div>{{ value.label }}</div>
              </div>
            </template>
            <template #option="{ option }: { option: SearchType }">
              <div class="flex items-center gap-2">
                <span class="material-icons text-gray-500">{{ option.icon }}</span>
                <div>{{ option.label }}</div>
              </div>
            </template>
          </Select>
          <InputText
            type="text"
            v-model="searchTerm"
            :placeholder="placeholderText"
            class="w-full !py-2.5 !border-0 !shadow-none !ring-0 text-base"
            @keydown.enter="() => performSearch(0)"
            @input="validationError = null"
          />
        </div>
        <div v-if="validationError" class="text-red-500 text-sm mt-2">
            {{ validationError }}
        </div>
        <!-- <div class="mt-4 flex items-center justify-between">
          <div v-if="isCompany" class="flex items-center gap-2">
              <ToggleSwitch v-model="filterActiveOnly" inputId="filter-active" />
              <label for="filter-active" class="text-sm font-medium text-slate-700">Somente Ativas</label>
          </div>
        </div> -->
      </div>
    </header>

          <!-- BENTO GRID LAYOUT -->
      <main class="grid grid-cols-1 lg:grid-cols-3 gap-8">

        <!-- MAIN CONTENT (LEFT COLUMN) -->
        <div class="lg:col-span-2">

          <!-- Loading Skeletons -->
          <div v-if="loading" class="space-y-4">
            <h2 class="text-lg font-semibold text-slate-900 px-1">Buscando...</h2>
            <div v-for="n in 3" :key="n" class="bg-white p-4 rounded-xl border border-slate-200 h-[88px]">
              <div class="flex items-center gap-4 h-full">
                <Skeleton shape="circle" size="3.5rem"></Skeleton>
                <div class="w-full space-y-2">
                  <Skeleton width="70%" height="1.2rem"></Skeleton>
                  <Skeleton width="40%"></Skeleton>
                </div>
              </div>
            </div>
          </div>

          <!-- Search Results or Empty State -->
          <div v-else-if="searchResults" class="space-y-4">
            <h2 class="text-lg font-semibold text-slate-900 px-1">Resultados da Busca ({{ totalResults }})</h2>
            <div v-if="searchResults.length > 0" class="space-y-4">
               <div v-for="item in searchResults" :key="(item as any).tax_id_full || (item as any).uuid" @click="onViewDetails(item)" :class="resultCardClasses">
                  <CardSearchResult v-if="isCompany" :company="item as CompanySearchResults" :show-status="!filterActiveOnly" />
                  <div v-else class="flex items-center gap-4 h-full">
                    <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                      <span class="material-icons text-3xl text-blue-600">person</span>
                    </div>
                    <div class="flex-grow">
                      <h3 class="font-bold text-slate-800 text-lg">{{ (item as PersonSearchResult).name }}</h3>
                      <p class="text-slate-500">CPF: {{ (item as PersonSearchResult).taxId }}</p>
                    </div>
                    <div class="flex flex-col items-end">
                        <span :class="['font-semibold text-sm px-2 py-1 rounded-full', (item as PersonSearchResult).taxIdStatusDescription === 'REGULAR' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800']">
                          {{ (item as PersonSearchResult).taxIdStatusDescription }}
                        </span>
                    </div>
                  </div>
               </div>
                <!-- Pagination -->
                <div v-if="totalResults > limit" class="pt-4">
                    <Paginator
                        v-model:first="offset"
                        :rows="limit"
                        :totalRecords="totalResults"
                        @page="onPageChange"
                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                    ></Paginator>
                </div>
            </div>
            <div v-else class="text-center py-16 px-4 bg-white rounded-lg border border-dashed">
                <span class="material-icons text-5xl text-slate-400">search_off</span>
                <h3 class="mt-4 text-xl font-semibold text-slate-800">Nenhum resultado</h3>
                <p class="mt-1 text-slate-500">Tente ajustar os termos da sua consulta.</p>
            </div>
          </div>

        </div>

        <!-- WIDGETS (RIGHT COLUMN) -->
        <div class="lg:col-span-1 space-y-8">
            <RecentActivityList
                :entityType="props.type"
                :title="isCompany ? 'Empresas Recentes' : 'Pessoas Recentes'"
            />
        </div>
      </main>
  </div>
</template>

<style scoped>
:deep(.p-paginator) {
    background: transparent;
    border: none;
    padding: 0;
}
:deep(.p-paginator-page), :deep(.p-paginator-next), :deep(.p-paginator-prev), :deep(.p-paginator-first), :deep(.p-paginator-last) {
    background-color: white;
    border: 1px solid #e2e8f0; /* slate-200 */
    border-radius: 0.5rem; /* rounded-lg */
    color: #475569; /* slate-600 */
    margin: 0 0.125rem;
    min-width: 2.5rem;
    height: 2.5rem;
    transition: all 0.2s;
}
:deep(.p-paginator-page:not(.p-highlight):hover), :deep(.p-paginator-next:not(.p-disabled):hover), :deep(.p-paginator-prev:not(.p-disabled):hover) {
    background-color: #f1f5f9; /* slate-100 */
    color: #1e293b; /* slate-800 */
}
/* Handle both .p-highlight and .p-paginator-page-selected for compatibility across PrimeVue 4 presets */
:deep(.p-paginator-page.p-highlight), :deep(.p-paginator-page.p-paginator-page-selected) {
    background-color: #4f46e5 !important; /* indigo-600 */
    color: white !important;
    border-color: #4f46e5 !important;
}
</style>
